const { v4: uuidv4 } = require("uuid");
import awsconfig from "@/aws-exports";

// ============================================================================
// CHATBOT - Self-contained chatbot widget
// ============================================================================
;(() => {
  if (window.__v0ChatbotInitialized) {
    console.log("Chatbot already initialized, skipping");
    return;
  }
  window.__v0ChatbotInitialized = true;

  let chatbotLandingConfig = process.env.CHATBOT_LANDING_CONFIG;
  // Normalize string, null
  try { chatbotLandingConfig = JSON.parse(chatbotLandingConfig); } catch (_) { chatbotLandingConfig = {}; };

  const default_config = {
    appsyncEndpoint: process.env.CHATBOT_API_URL || "your_chatbot_api_url",
    buttonBackground: process.env.BUTTON_BACKGROUND || 'linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.42 0.22 264) 100%)',
    debug: true,
    recaptchaSiteKey: process.env.RECAPTCHA_V3_SITE_KEY || "your_recaptcha_site_key",
    model: process.env.CHATBOT_MODEL || "your_chatbot_model",
    title: process.env.CHATBOT_TITLE || "Chatbot",
    headerButtonLabel: process.env.CHATBOT_HEADER_BUTTON_LABEL || "",
    headerButtonUrl: (process.env.CHATBOT_HEADER_BUTTON_URL || "").trim(),
    headerButtonTitle: process.env.CHATBOT_HEADER_BUTTON_TITLE || "",
    landingConfig: chatbotLandingConfig,
    landingUrl: window.location?.href
  };

  const getHost = url => { try { return new URL(url).host; } catch (_) {}; }

  class Chatbot {
    constructor(_config) {
      let config = { ...default_config, ..._config };
      console.log('config', config);
      const host = getHost(config.landingUrl);
      console.log('landingConfig', config.landingConfig[host]);
      // Overrides per landing_url
      config = { ...config, ...config.landingConfig[host] };
      this.isOpen = false;
      this.transcript = [];
      this.isLoading = false;
      this.recaptchaReady = false;
      this.config = config;
      this.contact_id = uuidv4();
      this.welcomed = false;

      // reCAPTCHA readiness promise (race-proof)
      this._recaptchaReadyPromise = null;

      this.log("Initializing chatbot...");
      this.init();
    }

    log(...args) {
      if (this.config && this.config.debug) {
        console.log("args", ...args);
      }
    }

    async init() {
      // Wait for DOM to be ready
      if (document.readyState === "loading") {
        await new Promise((resolve) => document.addEventListener("DOMContentLoaded", resolve));
      }

      this.setup();
    }

    setup() {
      this.log("Setting up chatbot...");
      this.loadRecaptcha();
      this.createUI();
      this.attachEventListeners();
      this.log("Chatbot setup complete");
    }

    // ========================================================================
    // reCAPTCHA loading / readiness (race-proof)
    // ========================================================================
    loadRecaptcha() {
      if (this._recaptchaReadyPromise) return this._recaptchaReadyPromise;

      this.log("Loading reCAPTCHA...");

      this._recaptchaReadyPromise = new Promise((resolve, reject) => {
        // If grecaptcha is already present, wait for ready()
        if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
          try {
            window.grecaptcha.ready(() => {
              this.recaptchaReady = true;
              this.log("reCAPTCHA already present + ready");
              resolve(true);
            });
            return;
          } catch (e) {
            this.log("grecaptcha.ready threw, will try script load", e);
          }
        }

        const scriptId = "v0-recaptcha-script";
        const existing = document.getElementById(scriptId);

        const onScriptLoad = () => {
          // Script loaded, but grecaptcha may not be attached yet
          const waitForGrecaptcha = () => {
            if (window.grecaptcha && typeof window.grecaptcha.ready === "function") {
              window.grecaptcha.ready(() => {
                this.recaptchaReady = true;
                this.log("reCAPTCHA loaded + ready");
                resolve(true);
              });
            } else {
              setTimeout(waitForGrecaptcha, 25);
            }
          };
          waitForGrecaptcha();
        };

        const onScriptError = () => {
          this.log("Error loading reCAPTCHA");
          reject(new Error("Error loading reCAPTCHA script"));
        };

        if (existing) {
          // If it already loaded (by us), proceed; otherwise attach listeners
          if (existing.getAttribute("data-loaded") === "true") {
            onScriptLoad();
          } else {
            existing.addEventListener("load", onScriptLoad, { once: true });
            existing.addEventListener("error", onScriptError, { once: true });
          }
          return;
        }

        const script = document.createElement("script");
        script.id = scriptId;
        script.async = true;
        script.defer = true;
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(this.config.recaptchaSiteKey)}`;
        script.onload = () => {
          script.setAttribute("data-loaded", "true");
          onScriptLoad();
        };
        script.onerror = onScriptError;
        document.head.appendChild(script);
      });

      return this._recaptchaReadyPromise;
    }

    withTimeout(promise, timeoutMs, label = "Operation") {
      return new Promise((resolve, reject) => {
        const id = setTimeout(() => {
          const err = new Error(`${label} timed out after ${timeoutMs}ms`);
          err.name = "TimeoutError";
          reject(err);
        }, timeoutMs);

        promise
          .then((v) => {
            clearTimeout(id);
            resolve(v);
          })
          .catch((e) => {
            clearTimeout(id);
            reject(e);
          });
      });
    }

    sleep(ms) {
      return new Promise((r) => setTimeout(r, ms));
    }

    async getRecaptchaToken() {
      try {
        const readyTimeout = Math.min(8000, this.config.timeoutMs ?? 15000);
        await this.withTimeout(this.loadRecaptcha(), readyTimeout, "reCAPTCHA readiness");

        const exec = () => window.grecaptcha.execute(this.config.recaptchaSiteKey, { action: "chat" });

        try {
          const token = await exec();
          this.log("Got reCAPTCHA token:", token.substring(0, 20) + "...");
          return token;
        } catch (error) {
          // retry once (helps with rare init edge cases)
          this.log("Error getting reCAPTCHA token, retrying once:", error);
          await this.sleep(200);
          const token = await exec();
          this.log("Got reCAPTCHA token:", token.substring(0, 20) + "...");
          return token;
        }
      } catch (error) {
        this.log("reCAPTCHA not ready or failed to load:", error);
        return null;
      }
    }

    // ============================================================================
    // FETCH WITH TIMEOUT (detectable via error.name === "TimeoutError")
    // ============================================================================
    async fetchWithTimeout(url, options = {}, timeoutMs = 3 * 60 * 1000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const res = await fetch(url, { ...options, signal: controller.signal });
        return res;
      } catch (err) {
        // In browsers, abort shows up as AbortError
        if (err && err.name === "AbortError") {
          const timeoutErr = new Error(`Request timed out after ${timeoutMs}ms`);
          timeoutErr.name = "TimeoutError";
          throw timeoutErr;
        }
        throw err;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    createUI() {
      // Create container
      const container = document.createElement("div");
      container.id = "v0-chatbot-container";
      container.innerHTML = `
        <style>
          /* Hide reCAPTCHA badge */
          .grecaptcha-badge {
            visibility: hidden;
            opacity: 0;
            pointer-events: none;
          }
          
          #v0-chatbot-container {
            position: fixed;
            bottom: 20px;
            right: 20px;
            z-index: 999999;
            font-family: system-ui, -apple-system, sans-serif;
          }
          
          #v0-chatbot-button {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            background: ${this.config.buttonBackground};
            border: none;
            cursor: pointer;
            box-shadow: 0 12px 32px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          #v0-chatbot-button:hover {
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 16px 40px rgba(0,0,0,0.25), 0 6px 16px rgba(0,0,0,0.2);
          }
          
          #v0-chatbot-button:active {
            transform: translateY(0) scale(1.02);
          }
          
          #v0-chatbot-button svg {
            width: 26px;
            height: 26px;
            stroke: white;
            fill: none;
            stroke-width: 2.5;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.15));
          }
          
          #v0-chatbot-window {
            position: fixed;
            bottom: 100px;
            right: 20px;
            width: min(600px, calc(100vw - 40px));
            min-height: min(375px, calc(100vh - 130px));
            max-height: calc(100vh - 130px);
            background: white;
            border-radius: 16px;
            box-shadow: 0 24px 72px rgba(0,0,0,0.2), 0 0 2px rgba(0,0,0,0.15);
            display: none;
            flex-direction: column;
            overflow: hidden;
            backdrop-filter: blur(10px);
            animation: scaleIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          #v0-chatbot-window.open {
            display: flex;
          }
          
          #v0-chatbot-header {
            background: linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.42 0.22 264) 100%);
            color: white;
            padding: 20px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid oklch(0.35 0.22 264);
          }
          
          #v0-chatbot-title {
            font-weight: 700;
            font-size: 18px;
            letter-spacing: -0.02em;
          }

          #v0-chatbot-book-btn {
            background: rgba(255,255,255,0.25);
            border: 1.5px solid rgba(255,255,255,0.5);
            border-radius: 8px;
            color: white;
            font-size: 12px;
            font-weight: 700;
            padding: 7px 14px;
            cursor: pointer;
            letter-spacing: 0.02em;
            transition: all 0.2s;
            flex-shrink: 0;
          }

          #v0-chatbot-book-btn:hover {
            background: rgba(255,255,255,0.4);
            transform: scale(1.03);
          }

          #v0-chatbot-controls {
            display: flex;
            gap: 6px;
          }
          
          .v0-chatbot-control-btn {
            background: rgba(255,255,255,0.2);
            border: none;
            border-radius: 8px;
            padding: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            backdrop-filter: blur(8px);
          }
          
          .v0-chatbot-control-btn:hover {
            background: rgba(255,255,255,0.35);
            transform: scale(1.05);
          }
          
          .v0-chatbot-control-btn:active {
            transform: scale(0.98);
          }
          
          .v0-chatbot-control-btn svg {
            width: 16px;
            height: 16px;
            stroke: white;
            fill: none;
            stroke-width: 2.5;
          }
          
          #v0-chatbot-messages {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            background: oklch(0.98 0 0);
          }
          
          #v0-chatbot-messages::-webkit-scrollbar {
            width: 8px;
          }
          
          #v0-chatbot-messages::-webkit-scrollbar-track {
            background: transparent;
          }
          
          #v0-chatbot-messages::-webkit-scrollbar-thumb {
            background: oklch(0.85 0 0);
            border-radius: 4px;
          }
          
          #v0-chatbot-messages::-webkit-scrollbar-thumb:hover {
            background: oklch(0.75 0 0);
          }
          
          .v0-message {
            display: flex;
            gap: 8px;
            animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .v0-message.ch_1 {
            justify-content: flex-end;
          }
          
          .v0-message-content {
            max-width: 80%;
            padding: 14px 16px;
            border-radius: 16px;
            line-height: 1.6;
            font-size: 15px;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
            box-shadow: 0 3px 12px rgba(0,0,0,0.08);
          }

          .v0-message.ch_0 .v0-message-content {
            max-width: 92%;
          }
          
          .v0-message.ch_1 .v0-message-content {
            background: linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.45 0.22 264) 100%);
            color: white;
            border-bottom-right-radius: 4px;
            border: 1px solid oklch(0.48 0.22 264);
          }
          
          .v0-message.ch_0 .v0-message-content {
            background: white;
            color: oklch(0.15 0 0);
            border-bottom-left-radius: 4px;
            border: 2px solid oklch(0.88 0 0);
          }
          
          #v0-chatbot-input-area {
            padding: 20px;
            border-top: 2px solid oklch(0.88 0 0);
            background: white;
            display: flex;
            gap: 10px;
          }
          
          #v0-chatbot-input {
            flex: 1;
            padding: 12px 16px;
            border: 2px solid oklch(0.85 0 0);
            border-radius: 12px;
            font-size: 15px;
            font-family: inherit;
            outline: none;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            background: oklch(0.99 0 0);
          }
          
          #v0-chatbot-input:focus {
            border-color: oklch(0.52 0.22 264);
            background: white;
            box-shadow: 0 0 0 4px oklch(0.52 0.22 264 / 0.15);
          }
          
          #v0-chatbot-send {
            background: linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.42 0.22 264) 100%);
            color: white;
            border: none;
            border-radius: 12px;
            padding: 12px 20px;
            cursor: pointer;
            font-weight: 700;
            font-size: 14px;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 4px 12px oklch(0.52 0.22 264 / 0.3);
          }
          
          #v0-chatbot-send:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px oklch(0.52 0.22 264 / 0.4);
          }
          
          #v0-chatbot-send:active:not(:disabled) {
            transform: translateY(0);
          }
          
          #v0-chatbot-send:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          
          .v0-typing-indicator {
            display: flex;
            gap: 5px;
            padding: 14px 16px;
          }
          
          .v0-typing-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: oklch(0.6 0 0);
            animation: typing 1.4s infinite ease-in-out;
          }
          
          .v0-typing-dot:nth-child(2) { animation-delay: 0.2s; }
          .v0-typing-dot:nth-child(3) { animation-delay: 0.4s; }
          
          @keyframes typing {
            0%, 60%, 100% { 
              transform: translateY(0);
              opacity: 0.5;
            }
            30% { 
              transform: translateY(-12px);
              opacity: 1;
            }
          }
          
          /* Modern link styling */
          .v0-message-content a {
            color: oklch(0.50 0.22 264);
            text-decoration: none;
            border-bottom: 1px solid oklch(0.50 0.22 264 / 0.4);
            transition: all 0.2s;
            font-weight: 600;
          }
          
          .v0-message-content a:hover {
            border-bottom-color: oklch(0.50 0.22 264);
            background: oklch(0.50 0.22 264 / 0.12);
          }
          
          .v0-message.ch_1 .v0-message-content a {
            color: white;
            border-bottom-color: rgba(255,255,255,0.5);
          }
          
          .v0-message.ch_1 .v0-message-content a:hover {
            border-bottom-color: white;
            background: rgba(255,255,255,0.15);
          }
          
          /* Modern code styling */
          .v0-message-content code {
            background: oklch(0.93 0 0);
            padding: 2px 6px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', monospace;
            font-size: 13px;
            border: 1.5px solid oklch(0.85 0 0);
            font-weight: 500;
          }
          
          /* Add reCAPTCHA attribution footer */
          #v0-recaptcha-attribution {
            padding: 8px 20px;
            background: oklch(0.96 0 0);
            border-top: 1px solid oklch(0.88 0 0);
            font-size: 11px;
            color: oklch(0.5 0 0);
            text-align: center;
          }
          
          #v0-recaptcha-attribution a {
            color: oklch(0.50 0.22 264);
            text-decoration: none;
          }
          
          #v0-recaptcha-attribution a:hover {
            text-decoration: underline;
          }

          /* Date picker calendar */
          .v0-date-picker {
            margin-top: 20px;
            padding: 20px 12px 10px;
            background: oklch(0.97 0 0);
            border: 1.5px solid oklch(0.88 0 0);
            border-radius: 12px;
            user-select: none;
          }

          .v0-date-picker-carousel {
            display: flex;
            align-items: flex-start;
            gap: 6px;
            padding-top: 0px;
          }

          .v0-date-picker-nav {
            background: none;
            border: none;
            width: 28px;
            height: 45px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            flex-shrink: 0;
            color: oklch(0.52 0.22 264);
            font-size: 16px;
            line-height: 1;
            padding-top: 3px;
            user-select: none;
          }

          .v0-date-picker-nav:hover {
            background: oklch(0.52 0.22 264);
            color: white;
            border-radius: 8px;
          }

          .v0-date-picker-nav:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .v0-date-picker-days {
            display: flex;
            gap: 6px;
            flex: 1;
            overflow: hidden;
          }

          .v0-date-col {
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: stretch;
            user-select: none;
          }

          .v0-date-chip {
            padding: 4px 3px;
            background: white;
            border: 2px solid oklch(0.52 0.22 264);
            border-radius: 8px;
            font-size: 11px;
            font-weight: 600;
            color: oklch(0.52 0.22 264);
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            text-align: center;
            min-width: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1px;
            box-sizing: border-box;
            align-self: flex-start;
            width: 100%;
            user-select: none;
          }

          .v0-date-chip .v0-dow {
            font-size: 10px;
            font-weight: 500;
            opacity: 0.7;
            text-transform: uppercase;
            letter-spacing: 0.03em;
            line-height: 1.0;
          }

          .v0-date-chip .v0-dom {
            font-size: 14px;
          }

          .v0-date-chip:hover {
            background: oklch(0.52 0.22 264);
            color: white;
          }

          .v0-date-chip.selected {
            background: oklch(0.52 0.22 264);
            color: white;
          }

          .v0-date-chip.dimmed {
            opacity: 0.4;
          }

          .v0-time-slot {
            margin-top: 10px;
            display: none;
          }

          .v0-time-slot.active {
            display: block;
          }

          .v0-col-times {
            display: flex;
            flex-direction: column;
            gap: 4px;
            overflow: hidden;
          }

          .v0-col-times-scroll {
            display: flex;
            flex-direction: column;
            gap: 4px;
            overflow: hidden;
            max-height: 140px;
          }

          .v0-col-times-nav {
            display: flex;
            justify-content: center;
            height: 18px;
          }

          .v0-col-times-nav-btn {
            background: none;
            border: none;
            border-radius: 4px;
            width: 28px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: oklch(0.52 0.22 264);
            font-size: 14px;
            transition: all 0.2s;
            user-select: none;
          }

          .v0-col-times-nav-btn:hover {
            background: oklch(0.52 0.22 264);
            color: white;
            border-radius: 4px;
          }

          .v0-col-times-nav-btn.hidden {
            opacity: 0.3;
            pointer-events: none;
          }

          .v0-col-time-chip {
            padding: 5px 6px;
            background: white;
            border: 1.5px solid oklch(0.52 0.22 264);
            border-radius: 8px;
            font-size: 12px;
            font-weight: 500;
            color: oklch(0.52 0.22 264);
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            width: 100%;
            text-align: center;
            box-sizing: border-box;
            user-select: none;
          }

          .v0-col-time-chip:hover {
            background: oklch(0.52 0.22 264);
            color: white;
          }

          .v0-col-time-chip.selected {
            background: oklch(0.52 0.22 264);
            color: white;
          }

          .v0-time-picker-label {
            font-size: 12px;
            font-weight: 600;
            color: oklch(0.45 0 0);
            margin-bottom: 8px;
          }

          .v0-time-picker-row {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
          }

          .v0-time-chip {
            padding: 6px 12px;
            background: white;
            border: 1.5px solid oklch(0.75 0 0);
            border-radius: 16px;
            font-size: 13px;
            font-weight: 500;
            color: oklch(0.35 0 0);
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .v0-time-chip:hover {
            background: oklch(0.52 0.22 264);
            border-color: oklch(0.52 0.22 264);
            color: white;
          }

          .v0-time-chip.selected {
            background: oklch(0.52 0.22 264);
            border-color: oklch(0.52 0.22 264);
            color: white;
          }

          /* iframe overlay — fixed, aligned to chat window position */
          .v0-iframe-overlay {
            position: fixed;
            bottom: 100px;
            right: 20px;
            width: min(600px, calc(100vw - 40px));
            height: calc(100vh - 130px);
            background: rgba(0,0,0,0.6);
            z-index: 10000000;
            display: flex;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(4px);
            border-radius: 16px;
          }

          .v0-iframe-modal {
            background: white;
            border-radius: 16px;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 24px 72px rgba(0,0,0,0.3);
          }

          #v0-chatbot-window.iframe-open {
            height: calc(100vh - 130px) !important;
            max-height: calc(100vh - 130px) !important;
          }

          .v0-iframe-header {
            background: linear-gradient(135deg, oklch(0.55 0.22 264) 0%, oklch(0.42 0.22 264) 100%);
            color: white;
            padding: 14px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-weight: 600;
            font-size: 15px;
            flex-shrink: 0;
          }

          .v0-iframe-header-close {
            background: rgba(255,255,255,0.2);
            border: none;
            border-radius: 8px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: white;
            font-size: 18px;
            transition: all 0.2s;
            flex-shrink: 0;
          }

          .v0-iframe-header-close:hover {
            background: rgba(255,255,255,0.35);
            transform: scale(1.05);
          }

          .v0-iframe-body {
            flex: 1;
            border: none;
          }
        </style>
        
        <button id="v0-chatbot-button" aria-label="Open chat">
          <svg viewBox="0 0 24 24">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
        
        <div id="v0-chatbot-window">
          <div id="v0-chatbot-header">
            <div style="display:flex;align-items:center;gap:10px;flex:1">
              <div id="v0-chatbot-title">${this.config.title}</div>
              ${this.config.headerButtonUrl && this.config.headerButtonUrl.trim() !== '' ? `<button id="v0-chatbot-book-btn">${this.config.headerButtonLabel || 'BOOK NOW'}</button>` : ''}
            </div>
            <div id="v0-chatbot-controls">
              <button class="v0-chatbot-control-btn" id="v0-clear-btn" aria-label="Clear chat">
                <svg viewBox="0 0 24 24">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
              <button class="v0-chatbot-control-btn" id="v0-close-btn" aria-label="Close chat">
                <svg viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
          <div id="v0-chatbot-messages"></div>
          <div id="v0-chatbot-input-area">
            <input 
              type="text" 
              id="v0-chatbot-input" 
              placeholder="Type your message..." 
              autocomplete="off"
            />
            <button id="v0-chatbot-send">Send</button>
          </div>
        </div>
      `;

      document.body.appendChild(container);
      this.log("UI created");
    }

    attachEventListeners() {
      const button = document.getElementById("v0-chatbot-button");
      const closeBtn = document.getElementById("v0-close-btn");
      const clearBtn = document.getElementById("v0-clear-btn");
      const sendBtn = document.getElementById("v0-chatbot-send");
      const input = document.getElementById("v0-chatbot-input");
      const bookBtn = document.getElementById("v0-chatbot-book-btn");

      button.addEventListener("click", () => this.toggleChat());
      if (bookBtn) {
        bookBtn.addEventListener("click", () => {
          this.showIframeOverlay(this.config.headerButtonUrl, this.config.headerButtonTitle || '');
        });
      }
      closeBtn.addEventListener("click", () => this.toggleChat());
      clearBtn.addEventListener("click", () => this.clearChat());
      sendBtn.addEventListener("click", () => this.sendMessage());
      input.addEventListener("keypress", (e) => {
        if (e.key === "Enter" && !this.isLoading) {
          this.sendMessage();
        }
      });

      // Add listener for our quick buttons
      const messages = document.getElementById("v0-chatbot-messages");
      messages.addEventListener("click", event => {
        const el = event.target;
        const button_clicked = event.target.getAttribute('button_clicked');
        const reply = event.target.getAttribute('reply');
        const title = event.target.getAttribute('title');
        if (reply === '1') {
          // Send the button reply
          this.sendMessage({
            button_clicked,
            text: title
          });
        }

        // Intercept link clicks — open in iframe overlay instead
        const anchor = event.target.closest('a');
        if (anchor) {
          event.preventDefault();
          const url = anchor.getAttribute('href');
          const linkTitle = anchor.textContent.trim() || anchor.getAttribute('title') || 'Link';
          this.showIframeOverlay(url, linkTitle);
        }
      });


      this.log("Event listeners attached");
    }

    toggleChat() {
      this.isOpen = !this.isOpen;
      const windowEl = document.getElementById("v0-chatbot-window");
      const input = document.getElementById("v0-chatbot-input");

      if (this.isOpen) {
        windowEl.classList.add("open");
        input.focus();
        this.log("Chat opened");
        if (!this.welcomed) {
          this.welcomed = true;
          this.sendMessage({
            op: 'start'
          });
        }
      } else {
        windowEl.classList.remove("open");
        this.log("Chat closed");
      }
    }

    clearChat() {
      this.transcript = [];
      const messagesDiv = document.getElementById("v0-chatbot-messages");
      messagesDiv.innerHTML = "";
      this.log("Chat cleared");
      const input = document.getElementById("v0-chatbot-input");
      if (input) {
        input.focus();
      }
    }

    async sendMessage({ button_clicked, op, suffix, text: _message } = {}) {
      const input = document.getElementById("v0-chatbot-input");
      const message = _message || input.value.trim();

      if (this.isLoading) return;
      // Prevent empty message when default op
      if (!op && message.length === 0 && !button_clicked) return;

      if (message) {
        this.log("Sending message:", message);
        // Add user message
        this.transcript.push({
          channel_label: "ch_1",
          text: message
        });
      }
      input.value = "";
      this.renderMessages();

      // Show loading
      this.isLoading = true;
      this.showTypingIndicator();

      try {
        // Get reCAPTCHA token
        const captchaToken = await this.getRecaptchaToken();
        if (!captchaToken) {
          throw new Error("Failed to get reCAPTCHA token");
        }

        // Strip out suffix key
        const _transcript = this.transcript.map(e => ({ ...e, suffix: undefined }));

        // Call AppSync GraphQL
        const response = await this.callAppSync({ button_clicked, captchaToken, op, transcript: _transcript });

        this.transcript = response.transcript || this.transcript;
        if (response.iframe) {
          this.showIframeOverlay(response.iframe.url, response.iframe.title);
        }
        if (suffix) {
          if (Array.isArray(suffix)) {
            this.transcript.push(...suffix);
          } else {
            // Add suffix key to last entry
            this.transcript[this.transcript.length - 1].suffix = suffix;
          }
        }
        this.log("Received response:", response);
      } catch (error) {
        this.log("Error:", error);

        const isTimeout = error && error.name === "TimeoutError";

        this.transcript.push({
          channel_label: "ch_0",
          text: isTimeout
            ? "Sorry — the request timed out. Please try again."
            : "Sorry, there was an error processing your request.",
        });
      } finally {
        this.isLoading = false;
        this.renderMessages();
        // Focus input after render completes
        setTimeout(() => input.focus(), 0);
      }
    }

    async callAppSync({ button_clicked, captchaToken, op, transcript }) {
      this.log("Calling AppSync with", transcript.length, "transcript");

      const variables = {
        ...(button_clicked ? { button_clicked } : undefined),
        captcha_token: captchaToken,
        contact_id: this.contact_id,
        landing_url: this.config.landingUrl,
        model: this.config.model || "gpt-4o-mini",
        op,
        transcript: transcript
      };

      this.log("GraphQL variables:", variables);

      const headers = {
        "Content-Type": "application/json",
      };

      if (this.config.appsyncApiKey) {
        headers["x-api-key"] = this.config.appsyncApiKey;
      }

      const timeoutMs = this.config.timeoutMs ?? 3 * 60 * 1000;

      const response = await this.fetchWithTimeout(
        this.config.appsyncEndpoint,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ dbCRUD_op: 'getCompletion', ...variables }),
        },
        timeoutMs,
      );

      const data = await response.json();
      this.log("AppSync response:", data);

      // Return silently -- Probably justan unsupported feature on my side
      if (!data) return {};

      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      const responseString = data.response;
      this.log("Raw response string:", responseString);

      let parsedResponse = JSON.parse(responseString);

      // Check if we got a string back (double-encoded JSON), parse again
      if (typeof parsedResponse === "string") {
        this.log("Response is double-encoded, parsing again");
        parsedResponse = JSON.parse(parsedResponse);
      }

      this.log("Parsed response:", parsedResponse);
      return parsedResponse;
    }

    showTypingIndicator() {
      const messagesDiv = document.getElementById("v0-chatbot-messages");
      const indicator = document.createElement("div");
      indicator.id = "v0-typing-indicator";
      indicator.className = "v0-message assistant";
      indicator.innerHTML = `
        <div class="v0-message-content">
          <div class="v0-typing-indicator">
            <div class="v0-typing-dot"></div>
            <div class="v0-typing-dot"></div>
            <div class="v0-typing-dot"></div>
          </div>
        </div>
      `;
      messagesDiv.appendChild(indicator);
      indicator.scrollIntoView({ behavior: "smooth", block: "end" });
    }

    showIframeOverlay(url, title) {
      // Only show if chat window is open
      const chatWindow = document.getElementById('v0-chatbot-window');
      if (!chatWindow || !chatWindow.classList.contains('open')) return;

      // Expand chat window to max height
      chatWindow.classList.add('iframe-open');

      // Remove existing overlay if any
      this.hideIframeOverlay();
      const overlay = document.createElement('div');
      overlay.className = 'v0-iframe-overlay';
      overlay.id = 'v0-iframe-overlay';
      overlay.innerHTML = `
        <div class="v0-iframe-modal">
          <div class="v0-iframe-header">
            <span>${title || ' '}</span>
            <button class="v0-iframe-header-close" id="v0-iframe-close-btn" aria-label="Close">
              &#x2715;
            </button>
          </div>
          <iframe class="v0-iframe-body" src="${url}" allow="camera; microphone; payment" sandbox="allow-scripts allow-forms allow-same-origin allow-modals allow-popups allow-popups-to-escape-sandbox allow-downloads" frameborder="0"></iframe>
        </div>
      `;
      document.body.appendChild(overlay);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.hideIframeOverlay();
      });
      document.getElementById('v0-iframe-close-btn').addEventListener('click', () => this.hideIframeOverlay());

      // Listen for postMessage from iframe (e.g., { type: 'chatbot-iframe-close' })
      const handleMessage = (e) => {
        if (e.data && e.data.type === 'chatbot-iframe-close') {
          this.hideIframeOverlay();
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);
    }

    hideIframeOverlay() {
      const overlay = document.getElementById('v0-iframe-overlay');
      if (overlay) overlay.remove();
      const input = document.getElementById('v0-chatbot-input');
      if (input) input.focus();
    }

    renderMessages() {
      const messagesDiv = document.getElementById("v0-chatbot-messages");

      // Remove typing indicator if exists
      const typingIndicator = document.getElementById("v0-typing-indicator");
      if (typingIndicator) {
        typingIndicator.remove();
      }

      // Get current number of rendered messages
      const currentMessageCount = messagesDiv.querySelectorAll(".v0-message").length;

      // Only render messages that haven't been rendered yet
      for (let i = currentMessageCount; i < this.transcript.length; i++) {
        const msg = this.transcript[i];
        const messageEl = document.createElement("div");
        messageEl.className = `v0-message ${msg.channel_label}`;
        if (!msg.text || msg.channel_label === 'tool') {
          // Create hidden element
          messageEl.style.display = 'none';
        } else {
          let displayText = msg.text;
          // We render the suffix but we don't include it as part of history
          if (msg.suffix) displayText += msg.suffix;

          // Check for date-picker blocks in ch_0 messages
          if (msg.channel_label === "ch_0" && displayText.includes('```date-picker')) {
            const parts = this.parseDatePicker(displayText);
            const contentDiv = document.createElement('div');
            contentDiv.className = 'v0-message-content';
            parts.forEach((part, i) => {
              if (part.type === 'datepicker') {
                contentDiv.insertAdjacentHTML('beforeend', part.content);
              } else {
                let content = part.content;
                // If next part is a datepicker, remove exactly 1 trailing newline (implicit \n from block)
                if (i + 1 < parts.length && parts[i + 1].type === 'datepicker') {
                  content = content.replace(/\n$/, '');
                }
                // If previous part was a datepicker, trim leading newlines (implicit \n from block — may be 1 or 2)
                if (i > 0 && parts[i - 1].type === 'datepicker') {
                  content = content.replace(/^\n+/, '\n');
                }
                contentDiv.insertAdjacentHTML('beforeend', this.parseMarkdown(content));
              }
            });
            messageEl.appendChild(contentDiv);
            // Initialize date picker handler after DOM is ready (defer to next tick so message is in DOM)
            setTimeout(() => {
            this.initDatePickerHandler();
          }, 0);
          } else {
            const formattedText =
              msg.channel_label === "ch_0" ? this.parseMarkdown(displayText) : this.escapeHtml(displayText);
            messageEl.innerHTML = `
              <div class="v0-message-content">${formattedText}</div>
            `;
          }
        }
        messagesDiv.appendChild(messageEl);
      }

      const lastMessage = messagesDiv.lastElementChild;
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }

    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }

    // ============================================================================
    // DATE PICKER PARSING
    // ============================================================================
    parseDatePicker(text) {
      const result = [];
      let lastIndex = 0;
      const openMarker = '```date-picker';
      const closeMarker = '```';

      let searchFrom = 0;
      while (true) {
        const openIdx = text.indexOf(openMarker, searchFrom);
        if (openIdx === -1) break;

        const afterOpen = openIdx + openMarker.length;
        const closeIdx = text.indexOf(closeMarker, afterOpen);
        if (closeIdx === -1) break;

        // Append text before the block
        if (openIdx > lastIndex) {
          result.push({ type: 'text', content: text.slice(lastIndex, openIdx) });
        }

        const jsonContent = text.slice(afterOpen, closeIdx).trim();
        try {
          const config = JSON.parse(jsonContent);
          const html = this.buildDatePickerHtml(config);
          result.push({ type: 'datepicker', content: html, name: config.name });
        } catch (e) {
          this.log('Error parsing date-picker block:', e);
          result.push({ type: 'text', content: text.slice(openIdx, closeIdx + closeMarker.length) });
        }

        lastIndex = closeIdx + closeMarker.length;
        searchFrom = lastIndex;
      }

      // Append remaining text
      if (lastIndex < text.length) {
        result.push({ type: 'text', content: text.slice(lastIndex) });
      }

      return result;
    }

    buildDatePickerHtml(config) {
      const id = 'dp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
      const label = config.label || 'Select a date and time';
      const options = config.options || [];

      return `<div class="v0-date-picker" id="${id}" data-name="${config.name || ''}" data-options="${encodeURIComponent(JSON.stringify(options))}">
          <div class="v0-date-picker-carousel">
            <button class="v0-date-picker-nav" id="${id}-prev" onclick="window.__chatbotDatePicker && window.__chatbotDatePicker.prev('${id}')">◀</button>
            <div class="v0-date-picker-days" id="${id}-days"></div>
            <button class="v0-date-picker-nav" id="${id}-next" onclick="window.__chatbotDatePicker && window.__chatbotDatePicker.next('${id}')">▶</button>
          </div>
        </div>`;
    }

    // ============================================================================
    // DATE PICKER INTERACTION
    // ============================================================================
    initDatePickerHandler() {
      if (window.__chatbotDatePicker) {
        // Handler exists — re-render all pickers in DOM
        document.querySelectorAll('.v0-date-picker[data-options]').forEach(picker => {
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options')));
          const startIdx = parseInt(picker.getAttribute('data-start') || '0', 10);
          window.__chatbotDatePicker.renderDays(picker.id, options, startIdx);
        });
        return;
      }
      window.__chatbotDatePicker = {
        activePicks: {},
        DAYS_SHOWN: 5,

        prev(pickerId) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options') || '[]'));
          const startIdx = parseInt(picker.getAttribute('data-start') || '0', 10);
          const newStart = Math.max(0, startIdx - this.DAYS_SHOWN);
          picker.setAttribute('data-start', newStart);
          this.renderDays(pickerId, options, newStart);
        },

        next(pickerId) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options') || '[]'));
          const startIdx = parseInt(picker.getAttribute('data-start') || '0', 10);
          const maxStart = Math.max(0, options.length - this.DAYS_SHOWN);
          const newStart = Math.min(maxStart, startIdx + this.DAYS_SHOWN);
          picker.setAttribute('data-start', newStart);
          this.renderDays(pickerId, options, newStart);
        },

        renderDays(pickerId, options, startIdx) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const daysDiv = document.getElementById(`${pickerId}-days`);
          const prevBtn = document.getElementById(`${pickerId}-prev`);
          const nextBtn = document.getElementById(`${pickerId}-next`);
          if (!daysDiv) return;

          const maxStart = Math.max(0, options.length - this.DAYS_SHOWN);
          prevBtn.disabled = startIdx === 0;
          nextBtn.disabled = startIdx >= maxStart;

          daysDiv.innerHTML = '';
          for (let i = 0; i < this.DAYS_SHOWN; i++) {
            const idx = startIdx + i;
            const opt = options[idx];
            if (!opt) {
              const placeholderCol = document.createElement('div');
              placeholderCol.className = 'v0-date-col';
              placeholderCol.style.width = `calc(100% / ${this.DAYS_SHOWN})`;
              daysDiv.appendChild(placeholderCol);
              continue;
            }
            const col = document.createElement('div');
            col.className = 'v0-date-col';
            col.style.width = `calc(100% / ${this.DAYS_SHOWN})`;

            const btn = document.createElement('button');
            btn.className = 'v0-date-chip';
            btn.setAttribute('data-date-index', idx);
            const d = new Date(opt.date + 'T00:00:00');
            const dow = d.toLocaleDateString('en-US', { weekday: 'short' });
            const domFormatted = `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
            btn.innerHTML = `<span class="v0-dow">${dow}</span><span class="v0-dom">${domFormatted}</span>`;
            btn.onclick = () => this.showTimesInColumn(pickerId, idx, col);
            col.appendChild(btn);

            // Add times vertically under this date (scrollable, max 4 shown)
            if (opt.times && opt.times.length) {
              const timesCol = document.createElement('div');
              timesCol.className = 'v0-col-times';
              timesCol.id = `${pickerId}-col-${idx}`;
              timesCol.setAttribute('data-option-idx', idx);
              timesCol.setAttribute('data-times', JSON.stringify(opt.times));

              const navTop = document.createElement('div');
              navTop.className = 'v0-col-times-nav';
              const upBtn = document.createElement('div');
              upBtn.className = 'v0-col-times-nav-btn';
              upBtn.textContent = '▲';
              upBtn.onclick = (e) => { e.stopPropagation(); this.scrollTimes(pickerId, idx, -1); };
              upBtn.classList.add('hidden');
              navTop.appendChild(upBtn);
              timesCol.appendChild(navTop);

              const scrollDiv = document.createElement('div');
              scrollDiv.className = 'v0-col-times-scroll';
              opt.times.slice(0, 4).forEach((t, ti) => {
                const tBtn = document.createElement('button');
                tBtn.className = 'v0-col-time-chip';
                tBtn.textContent = this.formatTime(t);
                tBtn.onclick = (e) => {
                  e.stopPropagation();
                  this.selectInColumn(pickerId, idx, ti, t, col);
                };
                scrollDiv.appendChild(tBtn);
              });
              timesCol.appendChild(scrollDiv);

              const navBot = document.createElement('div');
              navBot.className = 'v0-col-times-nav';
              const downBtn = document.createElement('div');
              downBtn.className = 'v0-col-times-nav-btn';
              downBtn.textContent = '▼';
              downBtn.onclick = (e) => { e.stopPropagation(); this.scrollTimes(pickerId, idx, 1); };
              if (opt.times.length <= 4) downBtn.classList.add('hidden');
              navBot.appendChild(downBtn);
              timesCol.appendChild(navBot);

              col.appendChild(timesCol);
            }

            daysDiv.appendChild(col);
          }

          delete this.activePicks[pickerId];
        },

        showTimes(pickerId, dateIndex) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options') || '[]'));
          const opt = options[dateIndex];
          if (!opt) return;

          // Update selected state
          picker.querySelectorAll('.v0-date-chip').forEach(chip => {
            chip.classList.toggle('selected', parseInt(chip.getAttribute('data-date-index'), 10) === dateIndex);
          });

          // Render times
          const timesDiv = document.getElementById(`${pickerId}-times`);
          if (!timesDiv || !opt.times || !opt.times.length) return;

          const times = opt.times.map((t, ti) =>
            `<button class="v0-time-chip" onclick="window.__chatbotDatePicker.select('${pickerId}', ${dateIndex}, ${ti}, '${t}')">${t}</button>`
          ).join('');

          timesDiv.innerHTML = `
            <div class="v0-time-picker-label">${opt.date}</div>
            <div class="v0-time-picker-row">${times}</div>
          `;
          timesDiv.classList.add('active');

          this.activePicks[pickerId] = { dateIndex };
        },

        formatTime(time24) {
          const [h, m] = time24.split(':');
          const hour = parseInt(h, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${m} ${ampm}`;
        },

        showTimesInColumn(pickerId, dateIndex, col) {
          // Highlight selected date chip in this column
          col.parentElement.querySelectorAll('.v0-date-chip').forEach(chip => chip.classList.remove('selected'));
          const btn = col.querySelector('.v0-date-chip');
          if (btn) btn.classList.add('selected');
          this.activePicks[pickerId] = { dateIndex };
        },

        scrollTimes(pickerId, dateIndex, direction) {
          const col = document.getElementById(`${pickerId}-col-${dateIndex}`);
          if (!col) return;
          const times = JSON.parse(col.getAttribute('data-times') || '[]');
          const scrollKey = `${pickerId}-${dateIndex}-scroll`;
          let scrollTop = parseInt(this.activePicks[scrollKey] || '0', 10);
          scrollTop = Math.max(0, Math.min(times.length - 4, scrollTop + direction));
          this.activePicks[scrollKey] = scrollTop;

          const scrollDiv = col.querySelector('.v0-col-times-scroll');
          const navBtns = col.querySelectorAll('.v0-col-times-nav-btn');
          if (!scrollDiv) return;

          scrollDiv.innerHTML = '';
          times.slice(scrollTop, scrollTop + 4).forEach((t, ti) => {
            const tBtn = document.createElement('button');
            tBtn.className = 'v0-col-time-chip';
            tBtn.textContent = this.formatTime(t);
            tBtn.onclick = (e) => {
              e.stopPropagation();
              const realIdx = scrollTop + ti;
              this.selectInColumn(pickerId, dateIndex, realIdx, t, col.closest('.v0-date-col'));
            };
            scrollDiv.appendChild(tBtn);
          });

          if (navBtns[0]) navBtns[0].classList.toggle('hidden', scrollTop === 0);
          if (navBtns[1]) navBtns[1].classList.toggle('hidden', scrollTop >= times.length - 4);
        },

        selectInColumn(pickerId, dateIndex, timeIndex, timeValue, col) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options') || '[]'));
          const opt = options[dateIndex];
          const dateStr = opt ? opt.date : '';
          const selectedValue = `${dateStr} ${timeValue}`;

          // Mark date and time as selected
          const dateChip = col.querySelector('.v0-date-chip');
          if (dateChip) dateChip.classList.add('selected');
          col.querySelectorAll('.v0-col-time-chip').forEach((chip, i) => {
            chip.classList.toggle('selected', i === timeIndex);
          });

          this.activePicks[pickerId] = { dateIndex, timeIndex, value: selectedValue };

          // Disable picker after selection
          setTimeout(() => {
            if (picker && picker.parentElement) {
              picker.style.opacity = '0.5';
              picker.style.pointerEvents = 'none';
            }
          }, 300);

          // Send the selected value
          const sendBtn = document.getElementById('v0-chatbot-send');
          const input = document.getElementById('v0-chatbot-input');
          if (sendBtn) {
            if (input) input.value = selectedValue;
            sendBtn.click();
          }
        },

        select(pickerId, dateIndex, timeIndex, timeValue) {
          const picker = document.getElementById(pickerId);
          if (!picker) return;
          const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options') || '[]'));
          const opt = options[dateIndex];
          const dateStr = opt ? opt.date : '';
          const selectedValue = `${dateStr} ${timeValue}`;

          // Mark time as selected
          const timesDiv = document.getElementById(`${pickerId}-times`);
          if (timesDiv) {
            timesDiv.querySelectorAll('.v0-time-chip').forEach((chip, i) => {
              chip.classList.toggle('selected', i === timeIndex);
            });
          }

          this.activePicks[pickerId] = { dateIndex, timeIndex, value: selectedValue };

          // Disable picker after selection
          setTimeout(() => {
            if (picker && picker.parentElement) {
              picker.style.opacity = '0.5';
              picker.style.pointerEvents = 'none';
            }
          }, 300);

          // Send the selected value
          const sendBtn = document.getElementById('v0-chatbot-send');
          if (sendBtn) {
            const input = document.getElementById('v0-chatbot-input');
            if (input) input.value = selectedValue;
            sendBtn.click();
          }
        }
      };

      // Render initial days for all pickers — start from closest future date
      const DAYS = window.__chatbotDatePicker.DAYS_SHOWN;
      document.querySelectorAll('.v0-date-picker[data-options]').forEach(picker => {
        const options = JSON.parse(decodeURIComponent(picker.getAttribute('data-options')));
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        let startIdx = 0;
        for (let i = 0; i < options.length; i++) {
          if (options[i].date >= todayStr) {
            startIdx = Math.max(0, i - (i % DAYS));
            break;
          }
        }

        picker.setAttribute('data-start', startIdx);
        window.__chatbotDatePicker.renderDays(picker.id, options, startIdx);
      });
    }

    parseMarkdown(text) {
      let html = text;

      const convertTables = (str) => {
        const lines = str.split("\n");
        const result = [];
        let i = 0;

        while (i < lines.length) {
          const line = lines[i];

          // Check if this line looks like a table row (contains |)
          if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
            const tableLines = [line];
            let j = i + 1;

            // Collect all consecutive table lines
            while (j < lines.length && lines[j].trim().startsWith("|") && lines[j].trim().endsWith("|")) {
              tableLines.push(lines[j]);
              j++;
            }

            // Convert table to HTML
            if (tableLines.length >= 2) {
              let tableHtml =
                '<table style="border-collapse: collapse; width: 100%; margin: 12px 0; border: 2px solid oklch(0.52 0.19 264); border-radius: 8px; overflow: hidden;">';

              // Header row
              const headerCells = tableLines[0].split("|").filter((cell) => cell.trim());
              tableHtml += '<thead><tr style="background: oklch(0.52 0.19 264); color: white;">';
              headerCells.forEach((cell) => {
                tableHtml += `<th style="padding: 12px; text-align: left; font-weight: 700; border-right: 1px solid oklch(0.52 0.19 264 / 0.3);">${cell.trim()}</th>`;
              });
              tableHtml += "</tr></thead>";

              // Body rows (skip separator line)
              tableHtml += "<tbody>";
              for (let k = 2; k < tableLines.length; k++) {
                const bodyCells = tableLines[k].split("|").filter((cell) => cell.trim());
                tableHtml += '<tr style="border-bottom: 1px solid oklch(0.52 0.19 264 / 0.2);">';
                bodyCells.forEach((cell) => {
                  tableHtml += `<td style="padding: 12px; border-right: 1px solid oklch(0.52 0.19 264 / 0.1);">${cell.trim()}</td>`;
                });
                tableHtml += "</tr>";
              }
              tableHtml += "</tbody></table>";

              result.push(tableHtml);
              i = j;
              continue;
            }
          }

          result.push(line);
          i++;
        }

        return result.join("\n");
      };

      html = convertTables(html);

      // Step 2: Convert markdown links [text](url) to HTML using string parsing
      const convertMarkdownLinks = (str) => {
        let result = "";
        let i = 0;

        while (i < str.length) {
          // Look for opening bracket
          if (str[i] === "[") {
            const bracketStart = i;
            let bracketEnd = -1;
            let parenStart = -1;
            let parenEnd = -1;

            // Find closing bracket
            for (let j = i + 1; j < str.length; j++) {
              if (str[j] === "]") {
                bracketEnd = j;
                break;
              }
            }

            // If we found closing bracket, look for opening paren
            if (bracketEnd !== -1 && bracketEnd + 1 < str.length && str[bracketEnd + 1] === "(") {
              parenStart = bracketEnd + 1;

              // Find closing paren
              for (let j = parenStart + 1; j < str.length; j++) {
                if (str[j] === ")") {
                  parenEnd = j;
                  break;
                }
              }
            }

            // If we found a complete markdown link
            if (bracketEnd !== -1 && parenStart !== -1 && parenEnd !== -1) {
              const linkText = str.substring(bracketStart + 1, bracketEnd);
              const url = str.substring(parenStart + 1, parenEnd);

              // Is it our custom reply: method?
              const _url = url.split(':');
              if ((_url[0] === 'reply') && _url[1]) {
                const uq = _url[1].replace(/^['"]|['"]$/g, '');
                result += `<button button_clicked="${linkText}" reply="1" style="margin:4px; background-color: transparent; border-radius: 8px; border: 2px solid black; padding: 4px;" title="${uq}">${linkText}</button>`;
              } else {
                // Create HTML link
                result += `<a href="${url.trim()}" target="_blank" rel="noopener noreferrer" style="color: oklch(0.52 0.19 264); text-decoration: none; border-bottom: 1px solid oklch(0.52 0.19 264 / 0.3); transition: all 0.2s; font-weight: 600; word-break: break-word; overflow-wrap: break-word;">${this.escapeHtml(
                  linkText.trim(),
                )}</a>`;
              }

              i = parenEnd + 1;
              continue;
            }
          }

          result += str[i];
          i++;
        }

        return result;
      };

      html = convertMarkdownLinks(html);

      // Step 3: Escape HTML in remaining text (but preserve our links and tables)
      const parts = html.split(/(<a[^>]*>.*?<\/a>|<table[^>]*>.*?<\/table>|<button[^>]*>.*?<\/button>)/gs);
      html = parts
        .map((part) => {
          if (part.startsWith("<a") || part.startsWith("<table") || part.startsWith("<button")) {
            return part; // Keep links and tables as-is
          }
          return this.escapeHtml(part);
        })
        .join("");

      // Step 4: Bold text
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 700;">$1</strong>');

      // Step 5: Italic text
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

      // Step 6: Line breaks
      html = html.replace(/\n\n+/g, "<br><br>");
      html = html.replace(/\n/g, "<br>");

      return html;
    }
  }

  // Initialize chatbot
  window.initChatbot = (config) => {
    console.log('window.initChatbot called');
    if (window.__chatbotInitialized__) {
      console.log("Chatbot already initialized");
      return;
    }
    console.log('window.initChatbot here 1');
    if (!process.env.CHATBOT_MODEL || process.env.CHATBOT_MODEL === 'openpath') {
      return;
    }
    window.__chatbotInitialized__ = true;
    new Chatbot(config);
  };

  // Auto-initialize without config if no explicit call
  if (!window.__chatbotInitialized__) {
    setTimeout(() => {
      if (!window.__chatbotInitialized__) {
        window.initChatbot();
      }
    }, 1000);
  }

  // Expose iframe overlay controls globally
  window.hideChatbotIframe = () => {
    const overlay = document.getElementById('v0-iframe-overlay');
    if (overlay) overlay.remove();
  };
})();
